import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import DOMPurify from 'dompurify';
import {
  Calendar, Clock, CheckCircle2, CalendarDays, Edit2, X, AlertTriangle, ExternalLink,
  FileText, Video, Layers, BookOpen, Megaphone, Mail, Film, ImageIcon
} from 'lucide-react';

const TYPE_META = {
  standard: { label: 'Post', icon: FileText, color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  carousel: { label: 'Carousel', icon: Layers, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  video_script: { label: 'Video Script', icon: Video, color: 'bg-red-50 text-red-700 border-red-200' },
  blog: { label: 'Blog', icon: BookOpen, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ad: { label: 'Ad', icon: Megaphone, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  email: { label: 'Email', icon: Mail, color: 'bg-sky-50 text-sky-700 border-sky-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const isVideo = (mime) => (mime || '').startsWith('video/');
const apiBase = process.env.REACT_APP_BACKEND_URL || '';
const mediaUrl = (u) => u?.startsWith('http') ? u : `${apiBase.replace(/\/$/, '')}${u || ''}`;

const renderBody = (post) => {
  const cj = post?.content_json || {};

  if (post.post_type === 'blog') {
    return (
      <article className="prose prose-zinc max-w-none text-sm">
        {cj.meta_description && (
          <p className="text-zinc-500 italic border-l-2 border-indigo-300 pl-3 !mt-0">{cj.meta_description}</p>
        )}
        {cj.intro && <p>{cj.intro}</p>}
        {(cj.sections || []).map((s, i) => (
          <div key={i}>
            <h3>{s.heading}</h3>
            <p className="whitespace-pre-wrap">{s.body}</p>
          </div>
        ))}
        {cj.conclusion && (
          <>
            <h3>Conclusion</h3>
            <p className="whitespace-pre-wrap">{cj.conclusion}</p>
          </>
        )}
        {cj.cta && (
          <div className="mt-4 p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-900 font-medium not-prose">
            👉 {cj.cta}
          </div>
        )}
      </article>
    );
  }

  if (post.post_type === 'ad') {
    const variants = cj.variants || [];
    return (
      <div className="space-y-3">
        {variants.length === 0 && <p className="text-sm text-zinc-500">No variants generated.</p>}
        {variants.map((v, idx) => (
          <div key={idx} className="rounded-lg border border-zinc-200 p-3 space-y-2">
            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Variant {idx + 1}</Badge>
            {v.headlines?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Headlines</p>
                <ul className="space-y-0.5 text-sm text-zinc-900">
                  {v.headlines.map((h, i) => <li key={i}>• {h}</li>)}
                </ul>
              </div>
            )}
            {v.descriptions?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Descriptions</p>
                <ul className="space-y-0.5 text-sm text-zinc-700">
                  {v.descriptions.map((d, i) => <li key={i}>• {d}</li>)}
                </ul>
              </div>
            )}
            {v.primary_text && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Primary text</p>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{v.primary_text}</p>
              </div>
            )}
            {v.notes && <p className="text-xs text-zinc-500 italic">💡 {v.notes}</p>}
          </div>
        ))}
      </div>
    );
  }

  if (post.post_type === 'email') {
    return (
      <div className="space-y-3">
        {(cj.subject_lines || []).length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Subject lines</p>
            <ul className="space-y-1">
              {cj.subject_lines.map((s, i) => (
                <li key={i} className="text-sm font-medium text-zinc-900">
                  {i === 0 && <Badge variant="outline" className="text-[10px] mr-2 bg-indigo-50 text-indigo-700 border-indigo-200">Primary</Badge>}
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {cj.preheader && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Preheader</p>
            <p className="text-sm italic text-zinc-700">{cj.preheader}</p>
          </div>
        )}
        <Tabs defaultValue="preview">
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="plain">Plain text</TabsTrigger>
          </TabsList>
          <TabsContent value="preview">
            <div className="rounded-md border border-zinc-200 p-3 bg-white">
              <div className="prose prose-zinc max-w-none text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cj.body_html || '') }} />
            </div>
          </TabsContent>
          <TabsContent value="plain">
            <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50">
              <pre className="text-sm text-zinc-800 whitespace-pre-wrap font-sans">{cj.body_plaintext || ''}</pre>
            </div>
          </TabsContent>
        </Tabs>
        {cj.cta_text && (
          <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 mb-0.5">Call-to-action</p>
            <p className="text-sm text-indigo-900 font-medium">{cj.cta_text}</p>
          </div>
        )}
      </div>
    );
  }

  if (post.post_type === 'carousel') {
    const slides = post.carousel_content || [];
    return (
      <div className="space-y-3">
        {post.caption && <p className="text-sm whitespace-pre-wrap text-zinc-800">{post.caption}</p>}
        {slides.map((s, i) => (
          <div key={i} className="rounded-lg border border-zinc-200 p-3">
            <Badge variant="outline" className="mb-2">Slide {i + 1}</Badge>
            {s.heading && <p className="font-semibold text-zinc-900">{s.heading}</p>}
            {s.body && <p className="text-sm text-zinc-700 whitespace-pre-wrap mt-1">{s.body}</p>}
          </div>
        ))}
      </div>
    );
  }

  if (post.post_type === 'video_script') {
    return (
      <div className="space-y-3">
        {post.caption && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Caption</p>
            <p className="text-sm whitespace-pre-wrap text-zinc-800">{post.caption}</p>
          </div>
        )}
        {post.video_script && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-1">Script</p>
            <pre className="text-sm whitespace-pre-wrap font-sans text-zinc-900 bg-zinc-50 p-3 rounded-lg border border-zinc-200">{post.video_script}</pre>
          </div>
        )}
      </div>
    );
  }

  // Standard social post
  return (
    <div className="space-y-3">
      <p className="text-sm whitespace-pre-wrap text-zinc-800">{post.caption}</p>
      {post.hashtags && (
        <p className="text-sm text-indigo-600 whitespace-pre-wrap">{post.hashtags}</p>
      )}
    </div>
  );
};

export const ViewPostDialog = ({ post, open, onClose, onEdit, onSchedule }) => {
  if (!post) return null;
  const meta = TYPE_META[post.post_type] || TYPE_META.standard;
  const TypeIcon = meta.icon;
  const generated = post.generated_image;
  const media = post.media || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="w-full h-full sm:h-auto sm:max-w-3xl lg:max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="view-post-dialog"
      >
        <DialogHeader className="shrink-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <Badge variant="outline" className={`flex items-center gap-1 ${meta.color}`}>
              <TypeIcon className="w-3 h-3" />
              <span className="text-[10px]">{meta.label}</span>
            </Badge>
            {post.platform && post.platform !== meta.label && (
              <Badge variant="outline" className="text-[10px]">{post.platform}</Badge>
            )}
            {post.status === 'published' ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Published
              </Badge>
            ) : post.status === 'failed' ? (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" title={post.publish_error || ''}>
                <AlertTriangle className="w-3 h-3 mr-1" /> Failed
              </Badge>
            ) : post.status === 'scheduled' && post.scheduled_for ? (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="w-3 h-3 mr-1" /> {fmtDate(post.scheduled_for)}
              </Badge>
            ) : (
              <Badge variant="outline">Draft</Badge>
            )}
          </div>
          <DialogTitle className="text-left text-lg sm:text-xl" data-testid="view-post-title">
            {post.post_title || '(untitled)'}
          </DialogTitle>
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Created {fmtDate(post.created_at)}
            </span>
            {post.audience && <span>·</span>}
            {post.audience && <span>For {post.audience}</span>}
            {post.tone && <span>·</span>}
            {post.tone && <span>{post.tone}</span>}
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6 my-2">
          <div className="space-y-4 pb-4" data-testid="view-post-body">
            {/* Publish error banner */}
            {post.status === 'failed' && post.publish_error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2" data-testid="view-post-publish-error">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">Publish failed</p>
                  <p className="text-xs mt-0.5 text-red-700">{post.publish_error}</p>
                </div>
              </div>
            )}

            {/* Published URL banner */}
            {post.status === 'published' && post.published_url && (
              <a
                href={post.published_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-800 hover:bg-emerald-100 transition-colors"
                data-testid="view-post-published-url"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="font-medium">View on {post.platform}</span>
                <ExternalLink className="w-3.5 h-3.5 ml-auto" />
              </a>
            )}

            {/* Media: AI-generated image */}
            {generated && (
              <img
                src={`data:image/png;base64,${generated}`}
                alt="AI generated"
                className="w-full max-h-[480px] object-contain rounded-lg border border-zinc-200 bg-zinc-50 mx-auto"
              />
            )}

            {/* Media: uploaded files */}
            {media.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {media.map((m) => {
                  const url = mediaUrl(m.url);
                  return (
                    <div key={m.id} className="relative rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 aspect-square">
                      {isVideo(m.mime) ? (
                        <video src={url} className="w-full h-full object-contain bg-zinc-900" controls preload="metadata" />
                      ) : (
                        <img src={url} alt={m.original_name} className="w-full h-full object-contain" />
                      )}
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] flex items-center gap-1">
                        {isVideo(m.mime) ? <Film className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
                        {Math.round(m.size / 1024)} KB
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Body */}
            {renderBody(post)}
          </div>
        </ScrollArea>

        <DialogFooter className="shrink-0 flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={onClose} data-testid="view-post-close-btn">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
          <div className="flex gap-2">
            {onEdit && (
              <Button variant="outline" onClick={() => onEdit(post)} data-testid="view-post-edit-btn">
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {onSchedule && (
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => onSchedule(post)}
                data-testid="view-post-schedule-btn"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                {post.scheduled_for ? 'Reschedule' : 'Schedule'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
